import { log } from '../log';
import { getTabsThatCanBeCommunicateWith } from '../utils';
import { isWorkspaceLinkingBridgeAction, publishLinkIntentFromLegacy } from '../workspaceBridge';
import { MATRIX } from './matrix';
import { notUndefined } from '@thinkalpha/common/util/notNullOrUndefined.js';
import { all, put, takeEvery } from 'redux-saga/effects';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import type { WorkspaceLinkingQuantitySelectedAction } from 'src/store/actions/widgets/linking';
import type { UserDoubleClickedPositionFromTableAction } from 'src/store/actions/widgets/positions';
import { updateTradeProQuantity } from 'src/store/actions/widgets/tradePro';

export function* handleQuantityChange(
    action: UserDoubleClickedPositionFromTableAction | WorkspaceLinkingQuantitySelectedAction,
) {
    // Only proceed if quantity is present
    if (action.quantity === undefined || action.quantity === null) return;

    const quantity = Math.abs(action.quantity);

    if (!isWorkspaceLinkingBridgeAction(action)) {
        yield* publishLinkIntentFromLegacy({
            type: 'quantity-selected',
            fromTabId: action.tabId,
            details: { quantity },
        });
    }

    const widgetsThatShouldReceiveUpdates = yield* getTabsThatCanBeCommunicateWith(action.tabId, MATRIX);
    if (!widgetsThatShouldReceiveUpdates.length) return;

    const widgetUpdates = widgetsThatShouldReceiveUpdates
        .map((x) => {
            const type = x.widget.type;
            switch (type) {
                case 'trade-pro':
                    return updateTradeProQuantity(x.id, quantity);
                case 'trade-pro-multi':
                case 'order-blotter':
                case 'messages':
                case 'positions':
                case 'executions':
                case 'orders':
                case 'lab':
                case 'accounts':
                case 'alpha-lens':
                case 'events':
                case 'filings':
                case 'news':
                case 'manual-review':
                case 'scanner':
                case 'screener':
                case 'searchalpha':
                case 'results':
                case 'table':
                case 'watchlist':
                case 'basket-trading':
                case 'alpha-eye':
                case 'notepad':
                case 'platforms':
                case 'symbols':
                case 'traders':
                case 'alerts':
                case 'tradingview-chart':
                case 'backtest':
                    if (TRACE) {
                        log.trace({
                            message: 'quantity change for widget not handled',
                            widget: x.widget,
                            quantity: action.quantity,
                        });
                    }
                    return undefined;
                default:
                    throw new UnreachableCaseError(type);
            }
        })
        .filter(notUndefined);

    if (widgetUpdates.length === 0) return;
    yield all(widgetUpdates.map((x) => put(x)));
}

export function* quantityLinkingSagas() {
    yield all([
        takeEvery(['userDoubleClickedPositionFromTable', 'workspaceLinkingQuantitySelected'], handleQuantityChange),
    ]);
}
